const SHEET_ID = import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL = import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID = import.meta.env.VITE_CHAT_ID;

let cached = { token: null, exp: 0 };
let sheetMeta = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getErrorMessage = async (response) => {
  try {
    const payload = await response.json();
    return payload.error?.message || payload.error || JSON.stringify(payload);
  } catch {
    return response.statusText || 'Unknown error';
  }
};

export async function getAccessToken() {
  if (cached.token && Date.now() < cached.exp) {
    return cached.token;
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chatId: CHAT_ID,
    }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unable to request Google Sheets access.');
  }

  cached = {
    token: data.access_token,
    exp: Date.now() + Math.max(data.expires_in - 60, 30) * 1000,
  };

  return cached.token;
}

export async function sheetsRequest(path, init = {}, retries = 2) {
  const token = await getAccessToken();
  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}${path}`,
    {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.status === 429 && retries > 0) {
    await wait((3 - retries) * 700);
    return sheetsRequest(path, init, retries - 1);
  }

  if (!response.ok) {
    throw new Error(`Google Sheets error: ${await getErrorMessage(response)}`);
  }

  if (response.status === 204) {
    return {};
  }

  return response.json();
}

export const getRows = async (range) => {
  const data = await sheetsRequest(`/values/${encodeURIComponent(range)}`);
  return data.values || [];
};

export const appendRow = (range, values) =>
  sheetsRequest(
    `/values/${encodeURIComponent(
      range
    )}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      body: JSON.stringify({
        values: [values],
      }),
    }
  );

export const updateRow = (range, values) =>
  sheetsRequest(
    `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      body: JSON.stringify({
        values: [values],
      }),
    }
  );

export async function getSheetMeta(force = false) {
  if (!force && sheetMeta) {
    return sheetMeta;
  }

  const data = await sheetsRequest('/?fields=sheets.properties');

  sheetMeta = Object.fromEntries(
    (data.sheets || []).map((sheet) => [
      sheet.properties.title,
      sheet.properties.sheetId,
    ])
  );

  return sheetMeta;
}

export async function getSheetIdByTitle(title) {
  let metadata = await getSheetMeta();

  if (metadata[title] !== undefined) {
    return metadata[title];
  }

  metadata = await getSheetMeta(true);
  return metadata[title];
}

const colLetter = (number) => {
  let result = '';
  let value = number;

  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }

  return result;
};

export async function ensureTab(title, headers) {
  const metadata = await getSheetMeta();

  if (metadata[title] === undefined) {
    await sheetsRequest('/:batchUpdate', {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            addSheet: {
              properties: {
                title,
              },
            },
          },
        ],
      }),
    });

    sheetMeta = null;
  }

  const headerRange = `${title}!A1:${colLetter(headers.length)}1`;
  const currentHeader = await getRows(headerRange);

  if (!currentHeader.length || !currentHeader[0] || currentHeader[0].length === 0) {
    await updateRow(headerRange, headers);
  }
}

export async function findRowIndexById(title, id) {
  const idColumn = await getRows(`${title}!A:A`);

  for (let index = 1; index < idColumn.length; index += 1) {
    if (idColumn[index]?.[0] === id) {
      return index + 1;
    }
  }

  return -1;
}

export async function deleteRow(title, id) {
  const sheetId = await getSheetIdByTitle(title);

  if (sheetId === undefined) {
    throw new Error(`Sheet tab not found: ${title}`);
  }

  const sheetRow = await findRowIndexById(title, id);

  if (sheetRow < 0) {
    throw new Error(`Application not found in ${title}.`);
  }

  const startIndex = sheetRow - 1;

  const result = await sheetsRequest('/:batchUpdate', {
    method: 'POST',
    body: JSON.stringify({
      requests: [
        {
          deleteDimension: {
            range: {
              sheetId,
              dimension: 'ROWS',
              startIndex,
              endIndex: startIndex + 1,
            },
          },
        },
      ],
    }),
  });

  sheetMeta = null;
  return result;
}
